/**
 * Bullet families a higher tier restates with a bigger number. Anchored so
 * "per-channel time slots" or "every workspace change" stay capabilities. A
 * bullet in no family is a capability and carries up to every higher tier.
 */
const FEATURE_FAMILIES: [family: string, pattern: RegExp][] = [
  ["channelsPerPost", /channels per post/i],
  ["mediaPerPost", /media items per post/i],
  ["channels", /^(up to )?[\d,]+ (social )?channels?\b/i],
  ["workspaces", /^[\d,]+ (client )?workspaces?\b/i],
  ["scheduling", /scheduled posts|unlimited scheduling/i],
  ["ai", /AI (credits|assistant)/i],
  ["analytics", /analytics/i],
  ["xPosts", /\bX posts\b/i],
  ["storage", /media storage/i],
  ["support", /support/i],
];

function featureFamily(feature: string): string {
  return (
    FEATURE_FAMILIES.find(([, pattern]) => pattern.test(feature))?.[0] ??
    feature
  );
}

type TierFeatures = {
  planGroup: string;
  rank: number;
  features: string[];
};

/**
 * Everything a tier includes. Polar benefits name only what a paid tier gates
 * or unlocks (see `scripts/setup-polar.mjs`), and product-wide capabilities
 * live on the Free entry, so a tier's full list folds in every tier below it:
 * a bullet replaces a lower tier's bullet of the same family in place
 * ("1 social channel" → "10 social channels"), and anything new is appended.
 *
 * Pass the whole catalog, Free included — /pricing and the in-app upgrade
 * dialog must show the same list for the same plan.
 */
export function cumulativeFeatures(
  plans: TierFeatures[],
  planGroup: string,
): string[] {
  const ordered = [...plans].sort((a, b) => a.rank - b.rank);
  const end = ordered.findIndex((plan) => plan.planGroup === planGroup);
  const byFamily = new Map<string, string>();

  for (const plan of ordered.slice(0, end + 1)) {
    for (const feature of plan.features) {
      // Map insertion order is kept on overwrite, so an upgrade stays put.
      byFamily.set(featureFamily(feature), feature);
    }
  }

  return [...byFamily.values()];
}
